import { useCallback, useMemo, useState } from 'react'

export type SortDirection = 'asc' | 'desc'

export const compareSortValues = (a: string | number, b: string | number) =>
  typeof a === 'string' && typeof b === 'string'
    ? a.localeCompare(b)
    : (a as number) - (b as number)

type TableSort<TRow, TColumn extends string> = {
  sortColumn: TColumn | null
  sortDirection: SortDirection
  toggleSort: (column: TColumn) => void
  sortedRows: TRow[]
}

/**
 * Client-side sorting for one stats table. Rows arrive fully loaded, so there
 * is nothing to refetch — sorting is a pure reordering.
 *
 * `getSortValue` must be a stable reference (a module-level function), since
 * the memoised sort depends on it.
 */
export const useTableSort = <TRow, TColumn extends string>(
  rows: TRow[] | null,
  getSortValue: (row: TRow, column: TColumn) => string | number,
): TableSort<TRow, TColumn> => {
  const [sortColumn, setSortColumn] = useState<TColumn | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  const toggleSort = useCallback(
    (column: TColumn) => {
      if (sortColumn === column) {
        setSortDirection(direction => (direction === 'asc' ? 'desc' : 'asc'))
      } else {
        setSortColumn(column)
        setSortDirection('asc')
      }
    },
    [sortColumn],
  )

  const sortedRows = useMemo(() => {
    if (!rows) return []
    if (!sortColumn) return rows

    return [...rows].sort(
      (a, b) =>
        compareSortValues(
          getSortValue(a, sortColumn),
          getSortValue(b, sortColumn),
        ) * (sortDirection === 'asc' ? 1 : -1),
    )
  }, [rows, sortColumn, sortDirection, getSortValue])

  return { sortColumn, sortDirection, toggleSort, sortedRows }
}
